import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-store.js';
import { money2, shortDate } from '../lib/format.js';
import { Badge, orderBadge } from '../components/badge.js';
import { IconSearch } from '../components/icons.js';
import { QueryError } from '../components/query-error.js';

const FILTERS = [
  { key: 'all', label: 'All', params: {} },
  { key: 'open', label: 'Open', params: { status: 'O', type: 'O' } },
  { key: 'quotes', label: 'Quotes', params: { type: 'Q' } },
  { key: 'invoiced', label: 'Invoiced', params: { status: 'P' } },
] as const;
type FilterKey = (typeof FILTERS)[number]['key'];

/**
 * Global "My orders" — the caller's recent orders across all customers (the API
 * scopes reps to their salesperson code; managers/admins see all). Answers
 * "where are my open orders" without digging into each customer.
 */
export function OrdersScreen() {
  const user = useAuth((s) => s.user);
  const [filter, setFilter] = useState<FilterKey>('open');
  const [q, setQ] = useState('');
  const [activeQ, setActiveQ] = useState('');
  const active = FILTERS.find((f) => f.key === filter)!;

  useEffect(() => {
    const t = setTimeout(() => setActiveQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const orders = useQuery({
    queryKey: ['orders-mine', filter, activeQ],
    queryFn: ({ signal }) =>
      api.salesOrders.listMine(
        { ...active.params, q: activeQ || undefined, limit: 100 },
        signal,
      ),
  });

  return (
    <>
      <div className="topbar">
        <div>
          <h2>Orders</h2>
          <div className="sub">
            {user?.role === 'rep' ? 'Your orders' : 'All recent orders'}
            {orders.data ? ` · ${orders.data.length}` : ''}
          </div>
        </div>
      </div>

      <div className="search-bar">
        <div className="search-wrap">
          <IconSearch size={16} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Order #, customer, or PO"
            aria-label="Search orders"
          />
        </div>
      </div>

      <div className="filter-chips">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`chip${filter === f.key ? ' active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={{ paddingTop: 8 }}>
        {orders.isPending && Array.from({ length: 6 }).map((_, i) => <div key={i} className="kd-skeleton" />)}
        {orders.isError && <QueryError message={orders.error.message} onRetry={() => void orders.refetch()} />}
        {orders.isSuccess && orders.data.length === 0 && (
          <div className="kd-muted" style={{ padding: 18 }}>
            {activeQ ? `No orders match "${activeQ}".` : `No ${filter === 'all' ? '' : filter} orders.`}
          </div>
        )}

        {orders.data?.map((o) => {
          const b = orderBadge(o.status, o.type);
          const total = Number(o.total) > 0 ? o.total : o.totalOrdered;
          return (
            <Link
              key={o.spireId}
              to="/clients/$id"
              params={{ id: String(o.customer.spireId) }}
              className="customer-row"
            >
              <div className="body">
                <div className="name">{o.customer.name}</div>
                <div className="meta" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  #{o.orderNo} <Badge variant={b.variant}>{b.label}</Badge>
                  {o.hold ? <Badge variant="danger">hold</Badge> : null}
                </div>
              </div>
              <div className="right">
                <div className="strong">{money2(total)}</div>
                <div>{shortDate(o.orderDate)}</div>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}
